/**
 * RefundRequestsDashboardPage — admin queue at /refund-requests
 * (LEVEL_12_REFUND_AND_CANCELLATION.md §17). Mirrors SupportDashboardPage's
 * UX: a status filter bar over a table, click-through to the detail page for
 * the actual Approve/Reject decision.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { HandCoins, Clock, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import {
  getRefundRequestStats,
  listRefundRequests,
  type AdminRefundRequest,
  type AdminRefundRequestStats,
  type AdminRefundRequestStatus,
} from '@/src/infrastructure/lib/api';

const STATUS_LABEL: Record<AdminRefundRequestStatus, string> = {
  PENDING_REVIEW: 'Pending review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<AdminRefundRequestStatus, string> = {
  PENDING_REVIEW: 'border-amber-500/40 text-amber-600 bg-amber-500/10',
  APPROVED: 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10',
  REJECTED: 'border-rose-500/40 text-rose-600 bg-rose-500/10',
  CANCELLED: 'border-slate-500/40 text-slate-500 bg-slate-500/10',
};

const STATUSES: AdminRefundRequestStatus[] = ['PENDING_REVIEW', 'APPROVED', 'REJECTED', 'CANCELLED'];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatPaise(paise: number | null) {
  if (paise === null) return 'Full remaining amount';
  return `₹${(paise / 100).toFixed(2)}`;
}

/**
 * `AdminRefundRequest.status` is terminal once APPROVED and never reflects
 * whether the linked execution Refund actually succeeded or failed at the
 * gateway — `refundStatus` is the only signal for that. Shown separately so
 * an admin scanning the queue can immediately see a failed refund instead
 * of a bare, misleadingly-final "Approved" badge (caught in round-2 review).
 */
function statusBadgeText(r: AdminRefundRequest): string {
  if (r.status === 'APPROVED' && r.refundStatus) {
    return `Approved — refund ${r.refundStatus.toLowerCase()}`;
  }
  return STATUS_LABEL[r.status];
}

export default function RefundRequestsDashboardPage() {
  const [allRequests, setAllRequests] = useState<AdminRefundRequest[]>([]);
  const [stats, setStats] = useState<AdminRefundRequestStats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  });
  const [status, setStatus] = useState<AdminRefundRequestStatus | ''>('PENDING_REVIEW');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  // True while the newest fetch is unresolved. A silent poll must never supersede it:
  // doing so would discard its response and leave `loading` stuck on.
  const inFlightRef = useRef(false);

  const fetchRequests = useCallback((silent = false) => {
    if (silent && inFlightRef.current) return;
    const curId = ++requestIdRef.current;
    inFlightRef.current = true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    // allSettled: a failed /stats must not hide the list (and vice versa) — the
    // list is the actionable part, the cards are a summary.
    Promise.allSettled([
      listRefundRequests({ status: '', limit: 200 }),
      getRefundRequestStats(),
    ])
      .then(([listRes, statsRes]) => {
        if (curId !== requestIdRef.current) return;
        if (listRes.status === 'fulfilled') setAllRequests(listRes.value.items);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value);
        const failure =
          listRes.status === 'rejected' ? listRes.reason : statsRes.status === 'rejected' ? statsRes.reason : null;
        if (!failure) {
          if (silent) setError(null);
        } else if (!silent) {
          setError(failure instanceof Error ? failure.message : 'Could not load refund requests');
        }
      })
      .finally(() => {
        if (curId !== requestIdRef.current) return;
        inFlightRef.current = false;
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchRequests();
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchRequests(true);
    }, 15000);
    return () => clearInterval(timer);
  }, [fetchRequests]);

  const items = status ? allRequests.filter((i) => i.status === status) : allRequests;

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl md:text-3xl font-display font-bold text-on-surface">Refund requests</h1>
        <p className="text-sm text-on-surface-variant">
          User-submitted refund requests awaiting review. Approving re-validates the amount fresh and
          hands off to the existing Razorpay refund pipeline — nothing here moves money directly.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<HandCoins className="w-5 h-5" />} label="Total requests" value={stats.total} />
        <StatCard icon={<Clock className="w-5 h-5 text-amber-600" />} label="Pending review" value={stats.pending} />
        <StatCard
          icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
          label="Approved"
          value={stats.approved}
        />
        <StatCard
          icon={<XCircle className="w-5 h-5 text-rose-600" />}
          label="Rejected"
          value={stats.rejected}
        />
      </div>

      <div className="sticky top-0 z-10 -mx-6 md:-mx-8 px-6 md:px-8 py-3 bg-surface-container-lowest/80 backdrop-blur-md border-b border-border-subtle flex flex-wrap gap-2 items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as AdminRefundRequestStatus | '')}
            className="rounded-full border border-border-subtle bg-surface px-3 py-1.5 text-xs font-medium"
          >
            <option value="">All statuses ({stats.total})</option>
            {STATUSES.map((s) => {
              const count =
                s === 'PENDING_REVIEW'
                  ? stats.pending
                  : s === 'APPROVED'
                  ? stats.approved
                  : s === 'REJECTED'
                  ? stats.rejected
                  : stats.cancelled;
              return (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]} ({count})
                </option>
              );
            })}
          </select>
          <span className="text-xs text-on-surface-variant">
            Showing {items.length} of {stats.total}
          </span>
        </div>

        <button
          type="button"
          onClick={() => fetchRequests(false)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border-subtle bg-surface hover:bg-surface-container-high transition-colors text-xs font-semibold text-on-surface disabled:opacity-50"
        >
          <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700">{error}</div>
      )}

      <div className="rounded-2xl border border-border-subtle overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-low/40 text-[10px] uppercase tracking-wider text-on-surface-variant">
            <tr>
              <th className="text-left py-2 px-3">Request</th>
              <th className="text-left py-2 px-3 hidden md:table-cell">Order</th>
              <th className="text-left py-2 px-3">Amount requested</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3 hidden lg:table-cell">Requested</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="text-center text-on-surface-variant py-12">
                  No refund requests match this filter.
                </td>
              </tr>
            )}
            {items.map((r) => (
              <motion.tr
                key={r.id}
                initial={{ opacity: 0, y: 2 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-t border-border-subtle hover:bg-surface-low/40"
              >
                <td className="py-2 px-3">
                  <Link to={`/refund-requests/${r.id}`} className="font-semibold text-on-surface hover:text-primary">
                    #{r.id.slice(0, 8)}
                  </Link>
                  <div className="text-[10px] text-on-surface-variant line-clamp-1 max-w-xs">{r.reason}</div>
                </td>
                <td className="py-2 px-3 hidden md:table-cell text-xs font-mono">
                  {r.orderNumber ? (
                    <Link to={`/orders/${r.purchaseId}`} className="underline text-primary hover:text-primary-dark">
                      {r.orderNumber}
                    </Link>
                  ) : (
                    `#${r.purchaseId.slice(0, 8)}`
                  )}
                </td>
                <td className="py-2 px-3 text-xs">{formatPaise(r.requestedAmountPaise)}</td>
                <td className="py-2 px-3">
                  <span
                    className={cn(
                      'inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                      r.status === 'APPROVED' && r.refundStatus === 'FAILED'
                        ? STATUS_TONE.REJECTED
                        : STATUS_TONE[r.status],
                    )}
                  >
                    {statusBadgeText(r)}
                  </span>
                </td>
                <td className="py-2 px-3 hidden lg:table-cell text-[10px] text-on-surface-variant">
                  {formatDate(r.createdAt)}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4">
      <div className="flex items-center gap-2 text-on-surface-variant">
        {icon}
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-display font-bold text-on-surface">{value}</p>
    </div>
  );
}
